import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../services/api";
import Navbar from "../components/Navbar";
import { buildProductDetailsPath } from "../utils/commerce";
import "./BuyerOrderDetails.css";

export default function BuyerOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Order Details | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "View order details on Balitah, a local Bruneian e-commerce platform.",
    );
  }, []);

  useEffect(() => {
    const loadOrderDetails = async () => {
      try {
        const data = await api.getOrder(id);
        if (data.success) {
          setOrder(data.data.order);
        } else {
          alert(data.message || "Order not found");
          navigate("/orders");
        }
      } catch (err) {
        console.error("Error fetching order:", err);
        alert("Failed to fetch order");
        navigate("/orders");
      } finally {
        setLoading(false);
      }
    };

    loadOrderDetails();
  }, [id, navigate]);

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
      processing: { color: "#3b82f6", bg: "#dbeafe", label: "Processing" },
      shipped: { color: "#8b5cf6", bg: "#ede9fe", label: "Shipped" },
      delivered: { color: "#10b981", bg: "#d1fae5", label: "Delivered" },
      cancelled: { color: "#ef4444", bg: "#fee2e2", label: "Cancelled" },
    };
    return (
      configs[status] || { color: "#6b7280", bg: "#f3f4f6", label: status }
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getOrderItemImageUrl = (item) => {
    const directImage =
      typeof item?.image === "string" ? item.image.trim() : "";
    if (
      directImage &&
      directImage !== "[object Object]" &&
      (directImage.startsWith("http") ||
        directImage.startsWith("/") ||
        directImage.startsWith("data:"))
    ) {
      return directImage;
    }

    const images = Array.isArray(item?.product?.images)
      ? item.product.images
      : [];
    if (images.length > 0) {
      const firstImage = [...images].sort((a, b) => {
        const aOrder =
          typeof a === "object" && a !== null ? Number(a.order ?? 0) : 0;
        const bOrder =
          typeof b === "object" && b !== null ? Number(b.order ?? 0) : 0;
        return aOrder - bOrder;
      })[0];

      if (typeof firstImage === "string" && firstImage.trim()) {
        return firstImage.trim();
      }

      if (
        firstImage &&
        typeof firstImage === "object" &&
        typeof firstImage.url === "string"
      ) {
        return firstImage.url.trim();
      }
    }

    if (typeof item?.product?.image === "string" && item.product.image.trim()) {
      return item.product.image.trim();
    }

    return "";
  };

  if (loading) {
    return (
      <div className="bod-page">
        <Navbar />
        <div className="bod-container">
          <div className="bod-loading">
            <div className="bod-spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="bod-page">
        <Navbar />
        <div className="bod-container">
          <div className="bod-error">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h2>Order not found</h2>
            <p>
              The order you're looking for doesn't exist or has been removed.
            </p>
            <button
              className="bod-btn bod-btn-primary"
              onClick={() => navigate("/orders")}
            >
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(order.status);

  return (
    <div className="bod-page">
      <Navbar />

      <div className="bod-container">
        {/* Back Button */}
        <button className="bod-back-btn" onClick={() => navigate("/orders")}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Orders
        </button>

        {/* Order Header Card */}
        <div className="bod-header-card">
          <div className="bod-header-top">
            <div className="bod-order-info">
              <span className="bod-order-label">Order</span>
              <h1 className="bod-order-id">
                #{order._id.slice(-8).toUpperCase()}
              </h1>
              <p className="bod-order-date">{formatDate(order.createdAt)}</p>
            </div>
            <div className="bod-order-status">
              <span
                className="bod-status-badge"
                style={{
                  color: statusConfig.color,
                  backgroundColor: statusConfig.bg,
                }}
              >
                {statusConfig.label}
              </span>
              <div className="bod-total">
                <span className="bod-total-label">Total</span>
                <span className="bod-total-amount">
                  BND {order.totalPrice.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Order Progress */}
          <div className="bod-progress">
            <div
              className={`bod-progress-step ${["pending", "processing", "shipped", "delivered"].includes(order.status) ? "active" : ""}`}
            >
              <div className="bod-step-dot"></div>
              <span>Ordered</span>
            </div>
            <div
              className={`bod-progress-line ${["processing", "shipped", "delivered"].includes(order.status) ? "active" : ""}`}
            ></div>
            <div
              className={`bod-progress-step ${["processing", "shipped", "delivered"].includes(order.status) ? "active" : ""}`}
            >
              <div className="bod-step-dot"></div>
              <span>Processing</span>
            </div>
            <div
              className={`bod-progress-line ${["shipped", "delivered"].includes(order.status) ? "active" : ""}`}
            ></div>
            <div
              className={`bod-progress-step ${["shipped", "delivered"].includes(order.status) ? "active" : ""}`}
            >
              <div className="bod-step-dot"></div>
              <span>Shipped</span>
            </div>
            <div
              className={`bod-progress-line ${order.status === "delivered" ? "active" : ""}`}
            ></div>
            <div
              className={`bod-progress-step ${order.status === "delivered" ? "active" : ""}`}
            >
              <div className="bod-step-dot"></div>
              <span>Delivered</span>
            </div>
          </div>
        </div>

        <div className="bod-content-grid">
          {/* Items Section */}
          <div className="bod-section bod-items-section">
            <div className="bod-section-header">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              <h2>Items Ordered</h2>
              <span className="bod-item-count">
                {order.items.length} item{order.items.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="bod-items-list">
              {order.items.map((item) => {
                const productId =
                  typeof item.product === "string"
                    ? item.product
                    : item.product?._id;
                const canOpenProduct = Boolean(productId);
                const itemImage = getOrderItemImageUrl(item);
                const productPath = buildProductDetailsPath({
                  _id: productId,
                  name: item.name,
                  categories: item.product?.categories,
                  category: item.product?.category,
                });

                return (
                  <div
                    key={item._id}
                    className={`bod-item-card ${canOpenProduct ? "clickable" : ""}`}
                    role={canOpenProduct ? "button" : undefined}
                    tabIndex={canOpenProduct ? 0 : -1}
                    onClick={() => canOpenProduct && navigate(productPath)}
                    onKeyDown={(e) => {
                      if (
                        canOpenProduct &&
                        (e.key === "Enter" || e.key === " ")
                      ) {
                        e.preventDefault();
                        navigate(productPath);
                      }
                    }}
                  >
                    <div className="bod-item-image">
                      {itemImage ? (
                        <img src={itemImage} alt={item.name} />
                      ) : (
                        <div className="bod-no-image">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          >
                            <rect
                              x="3"
                              y="3"
                              width="18"
                              height="18"
                              rx="2"
                              ry="2"
                            />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="bod-item-info">
                      <h3 className="bod-item-name">{item.name}</h3>
                      {item.variantName && (
                        <p className="bod-item-variant">
                          Variation: {item.variantName}
                        </p>
                      )}
                      <p className="bod-item-qty">Quantity: {item.quantity}</p>
                      <p className="bod-item-price">
                        BND {item.price.toFixed(2)} each
                      </p>
                    </div>
                    <div className="bod-item-total">
                      <span>BND {(item.price * item.quantity).toFixed(2)}</span>
                      {canOpenProduct && (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar */}
          <div className="bod-sidebar">
            {/* Payment Info */}
            <div className="bod-section bod-payment-section">
              <div className="bod-section-header">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                <h2>Payment</h2>
              </div>
              <div className="bod-info-row">
                <span className="bod-info-label">Method</span>
                <span className="bod-info-value">
                  {order.paymentMethod.replace("_", " ")}
                </span>
              </div>
              <div className="bod-info-row">
                <span className="bod-info-label">Subtotal</span>
                <span className="bod-info-value">
                  BND {order.totalPrice.toFixed(2)}
                </span>
              </div>
              <div className="bod-info-row">
                <span className="bod-info-label">Shipping</span>
                <span className="bod-info-value bod-free">Free</span>
              </div>
              <div className="bod-info-row bod-total-row">
                <span className="bod-info-label">Total</span>
                <span className="bod-info-value">
                  BND {order.totalPrice.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bod-section bod-shipping-section">
              <div className="bod-section-header">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <h2>Shipping Address</h2>
              </div>
              <div className="bod-address">
                <p>{order.shippingAddress.street}</p>
                <p>
                  {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                  {order.shippingAddress.postalCode}
                </p>
                <p>{order.shippingAddress.country}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
